import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { getQuestionUrl, getLearningObjectiveUrl } from 'utils/navigation';
import withNavigation from 'components/hocs/withNavigation';
import {
  getBackground,
  isAllQuestionsOnOnePage,
  getTimerEnabled,
  getTimerLimit,
  isQuestionPoolEnabled
} from 'store/settings/selectors';
import { getCourseTitle, getCourseIntroductions } from 'store/course/selectors';
import {
  getSections,
  affectsProgress,
  getAffectProgressQuestionsCountInSection,
  getAnsweredSectionQuestionsCount,
  getCorrectlyAffectProgressQuestionsCountInSection
} from 'store/sections/selectors';
import SectionsListComponent from 'components/sections/SectionsList';
import CourseInformation from 'components/sections/CourseInformation';
import QuestionPool from 'components/sections/QuestionPool/QuestionPool';
import { localize } from 'core/localization';
import { RootAppState } from 'store/types';
import * as courseTimerActions from 'store/timer/actions';
import { getTimeFromMilliseconds } from 'utils/timer';
import { getTimerElapsed, getTimerStatus } from 'store/timer/selectors';
import * as courseQuestionActions from 'store/questions/actions';
import { TimerStatus } from 'constants/timer';
import {
  SectionsPageContainer,
  SectionListContainer,
  SectionsPageSidebar,
  SectionsPageContent
} from './SectionsPage.styled';

import * as navigationActions from '../../../store/navigation/actions';
import SkipToContent from '../../../components/common/SkipToContent';
import { SKIP_TO_CONTENT_IDS } from 'constants/common';
import { getPopupVisibility } from '../../../store/popup/selectors';

type SectionsProps = {
  sections: any[];
  navigateToUrl(url: string): void;
  navigateToPrevPage(): void;
  checkIsPreviousPageResults(): boolean;
  updateClickedID(url: string): void;
  isAccessLimited: boolean;
  theme: { [key: string]: any };
  background: { [key: string]: any };
  title: string;
  isQuestionPool: boolean;
  scrollToId(id: string): void;
  isCourseOnOnePage: boolean;
  navigateToOnePage(id: string): void;
  timerActions: { [key: string]: any };
  questionActions: { [key: string]: any };
  isTimerEnabled: boolean;
  timerStatus: TimerStatus;
  timeLimit: number;
  timeElapsed: number;
  popupExpanded: boolean;
};

export class Sections extends React.Component<SectionsProps, {}> {
  sectionRefs: { [key: string]: any };
  questionPoolButtonRef: any;

  constructor(props: SectionsProps) {
    super(props);
    this.sectionRefs = [];
    this.questionPoolButtonRef = React.createRef();
  }

  setSectionRefs = (ref: any) => {
    this.sectionRefs.push(ref);
  };

  focusFirstSection = () => this.sectionRefs[0].focus();

  focusQuestionPoolButton = () => {
    const element = document.getElementById(SKIP_TO_CONTENT_IDS.QUESTION_POOL_START_BUTTON);
    if (element) {
      element.focus();
    }
  };

  focusElement = () => {
    const { isQuestionPool } = this.props;
    return isQuestionPool ? this.focusQuestionPoolButton() : this.focusFirstSection();
  };

  navigateToQuestion = (sectionId: string, questionId: string, isCourseOnOnePage: boolean) => {
    const { isTimerEnabled, timerActions, questionActions, timeElapsed, timerStatus } = this.props;
    if (
      isTimerEnabled &&
      (timerStatus === TimerStatus.NOT_STARTED || timerStatus === TimerStatus.RESET)
    ) {
      timerActions.startTimer();
      timerActions.setTimerElapsed(timeElapsed);
    }
    if (isCourseOnOnePage) {
      this.props.navigateToUrl(getQuestionUrl(sectionId, questionId));
      this.props.scrollToId(questionId);
      questionActions.markAsOpened(questionId);
    } else {
      this.props.navigateToUrl(getQuestionUrl(sectionId, questionId));
    }
  };

  navigateToLearningObjective = (sectionId: string, isCourseOnOnePage: boolean) => {
    if (isCourseOnOnePage) {
      this.props.navigateToUrl(getLearningObjectiveUrl(sectionId));
      this.props.scrollToId(sectionId);
    } else {
      this.props.navigateToUrl(getLearningObjectiveUrl(sectionId));
    }
  };

  navigateToPrevPage = () => {
    const { navigateToPrevPage } = this.props;
    navigateToPrevPage();
  };

  componentDidMount() {
    document.title = `${localize('[section page title]')} | ${this.props.title}`;
  }

  render() {
    const { sections, background, isQuestionPool, isCourseOnOnePage, popupExpanded } = this.props;
    return (
      <>
        {!popupExpanded && <SkipToContent onClick={this.focusElement} />}
        <SectionsPageContainer>
          {sections.length > 0 && (
            <SectionsPageContent background={background}>
              {isQuestionPool ? (
                <SectionListContainer>
                  <QuestionPool
                    navigateToQuestion={this.navigateToQuestion}
                    navigateToPreviousPage={this.navigateToPrevPage}
                    checkIsPreviousPageResults={this.props.checkIsPreviousPageResults}
                    isCourseOnOnePage={isCourseOnOnePage}
                  />
                </SectionListContainer>
              ) : (
                <SectionListContainer>
                  <SectionsListComponent
                    sections={sections}
                    navigateToQuestion={this.navigateToQuestion}
                    navigateToLearningObjective={this.navigateToLearningObjective}
                    isCourseOnOnePage={isCourseOnOnePage}
                    setSectionRefs={this.setSectionRefs}
                  />
                </SectionListContainer>
              )}
            </SectionsPageContent>
          )}
          <SectionsPageSidebar background={background}>
            <CourseInformation sections={sections} />
          </SectionsPageSidebar>
        </SectionsPageContainer>
      </>
    );
  }
}

function mapStateToProps(state: RootAppState) {
  return {
    title: getCourseTitle(state),
    introductions: getCourseIntroductions(state),
    sections: getSections(state).map(section => ({
      ...section,
      affectsProgress: affectsProgress(state, section.id),
      affectProgressQuestionsCountInSection: getAffectProgressQuestionsCountInSection(
        state,
        section.id
      ),
      answeredSectionQuestionsCount: getAnsweredSectionQuestionsCount(state, section.id),
      correctlyAffectProgressQuestionsCountInSection: getCorrectlyAffectProgressQuestionsCountInSection(
        state,
        section.id
      )
    })),
    background: getBackground(state),
    isQuestionPool: isQuestionPoolEnabled(state),
    isCourseOnOnePage: isAllQuestionsOnOnePage(state),
    timeLimit: getTimeFromMilliseconds(getTimerLimit(state), 'min') as number,
    timerStatus: getTimerStatus(state),
    isTimerEnabled: getTimerEnabled(state),
    timeElapsed: getTimerElapsed(state),
    popupExpanded: getPopupVisibility(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    navigationActions: bindActionCreators(navigationActions, dispatch),
    questionActions: bindActionCreators(courseQuestionActions, dispatch),
    timerActions: bindActionCreators(courseTimerActions, dispatch)
  };
}

export default withNavigation(connect(mapStateToProps, mapDispatchToProps)(Sections));
